export const SUPPORTED_LOCALES = ['en', 'de', 'fr'] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

export const DEFAULT_LOCALE: SupportedLocale = 'en';
export const LANGUAGE_COOKIE_NAME = 'lang';
export const LANGUAGE_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

const LOCALE_PREFIX_RE = /^\/([a-z]{2})(\/.*|$)/i;

export const MARKETING_PATH_PREFIXES = [
  '/narrafix-studio-demo',
  '/data-privacy',
  '/terms-of-service',
  '/imprint',
] as const;
type MarketingPathPrefix = (typeof MARKETING_PATH_PREFIXES)[number];

const LOCALIZED_MARKETING_PREFIXES: Record<
  MarketingPathPrefix,
  Partial<Record<SupportedLocale, string>>
> = {
  '/data-privacy': {
    de: '/datenschutz',
  },
  '/terms-of-service': {
    de: '/agb',
  },
  '/imprint': {
    de: '/impressum',
  },
};

type CookieWriter = {
  cookies: {
    set: (
      name: string,
      value: string,
      options: {
        sameSite: 'lax';
        path: '/';
        secure: boolean;
        maxAge: number;
      },
    ) => void;
  };
};

export function isSupportedLocale(
  value: string | undefined,
): value is SupportedLocale {
  return SUPPORTED_LOCALES.includes(value as SupportedLocale);
}

export function resolveLocaleOrDefault(
  value: string | undefined,
): SupportedLocale {
  return isSupportedLocale(value) ? value : DEFAULT_LOCALE;
}

export function parseLocaleFromPath(pathname: string) {
  const match = LOCALE_PREFIX_RE.exec(pathname);
  const localeInPath = match?.[1]?.toLowerCase();

  if (!isSupportedLocale(localeInPath)) {
    return {
      localeInPath: undefined,
      unprefixedPathname: pathname,
    };
  }

  const rest = match?.[2] ?? '/';

  return {
    localeInPath,
    unprefixedPathname: rest === '' ? '/' : rest,
  };
}

export function addLocalePrefix(pathname: string, locale: SupportedLocale) {
  if (locale === DEFAULT_LOCALE) {
    return removeLocalePrefix(pathname);
  }

  const unprefixedPathname = removeLocalePrefix(pathname);

  if (unprefixedPathname === '/') {
    return `/${locale}`;
  }

  return `/${locale}${unprefixedPathname}`;
}

export function removeLocalePrefix(pathname: string) {
  const { unprefixedPathname } = parseLocaleFromPath(pathname);
  return unprefixedPathname;
}

export function isMarketingPath(pathname: string) {
  const unprefixedPathname = toCanonicalMarketingPathname(removeLocalePrefix(pathname));

  if (unprefixedPathname === '/') {
    return true;
  }

  return MARKETING_PATH_PREFIXES.some(
    (prefix) =>
      unprefixedPathname === prefix ||
      unprefixedPathname.startsWith(`${prefix}/`),
  );
}

export function getLocalizedMarketingPathname(
  pathname: string,
  locale: SupportedLocale,
) {
  const unprefixedPathname = toCanonicalMarketingPathname(removeLocalePrefix(pathname));

  if (!isMarketingPath(unprefixedPathname)) {
    return unprefixedPathname;
  }

  const localizedPathname = toLocalizedMarketingPathname(unprefixedPathname, locale);

  return locale === DEFAULT_LOCALE
    ? localizedPathname
    : addLocalePrefix(localizedPathname, locale);
}

export function toCanonicalMarketingPathname(
  pathname: string,
  preferredLocale?: SupportedLocale,
) {
  for (const prefix of MARKETING_PATH_PREFIXES) {
    const localesToCheck = preferredLocale
      ? [preferredLocale]
      : (SUPPORTED_LOCALES as readonly SupportedLocale[]);

    for (const locale of localesToCheck) {
      const localizedPrefix = getLocalizedPrefix(prefix, locale);
      const canonicalized = swapPrefix(pathname, localizedPrefix, prefix);

      if (canonicalized) {
        return canonicalized;
      }
    }
  }

  return pathname;
}

function toLocalizedMarketingPathname(pathname: string, locale: SupportedLocale) {
  for (const prefix of MARKETING_PATH_PREFIXES) {
    const localizedPrefix = getLocalizedPrefix(prefix, locale);
    const localizedPathname = swapPrefix(pathname, prefix, localizedPrefix);

    if (localizedPathname) {
      return localizedPathname;
    }
  }

  return pathname;
}

function getLocalizedPrefix(prefix: MarketingPathPrefix, locale: SupportedLocale) {
  return LOCALIZED_MARKETING_PREFIXES[prefix]?.[locale] ?? prefix;
}

function swapPrefix(pathname: string, sourcePrefix: string, targetPrefix: string) {
  if (pathname === sourcePrefix) {
    return targetPrefix;
  }

  if (pathname.startsWith(`${sourcePrefix}/`)) {
    return `${targetPrefix}${pathname.slice(sourcePrefix.length)}`;
  }

  return null;
}

export function setLanguageCookie(
  response: CookieWriter,
  locale: SupportedLocale,
  secure: boolean,
) {
  response.cookies.set(LANGUAGE_COOKIE_NAME, locale, {
    sameSite: 'lax',
    path: '/',
    secure,
    maxAge: LANGUAGE_COOKIE_MAX_AGE_SECONDS,
  });
}

export function detectPreferredLocaleFromAcceptLanguage(
  acceptLanguageHeader: string | null | undefined,
) {
  if (!acceptLanguageHeader) {
    return DEFAULT_LOCALE;
  }

  const tags = acceptLanguageHeader
    .toLowerCase()
    .split(',')
    .map((part) => part.split(';')[0]?.trim())
    .filter((part): part is string => Boolean(part));

  for (const tag of tags) {
    if (isSupportedLocale(tag)) {
      return tag;
    }

    const baseTag = tag.split('-')[0];

    if (isSupportedLocale(baseTag)) {
      return baseTag;
    }
  }

  return DEFAULT_LOCALE;
}
